import React, { useState, useRef, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAppContext } from '../context/AppContext';
import { db } from '../utils/db';
import { calcGoalMonthly, getMonthsUntil } from '../utils/guiltFree';
import { formatINR } from '../utils/leakBuckets';
import { getAllCategories, getSubcategories } from '../utils/categorization';
import {
    isProUnlocked,
    buildVaultPayload,
    encryptVault,
    decryptVault,
    restoreVault,
    downloadBlob,
    vaultFilename,
    downloadCsv,
} from '../utils/vault';
import { handleRetag } from '../utils/sync';
import WaitlistModal from './WaitlistModal';
import ProCheckoutModal from './ProCheckoutModal';
import { apiConfigured } from '../utils/api';
import { getSessionToken, getProAccount, isProEntitled } from '../utils/entitlement';
import {
    pushSupported,
    enablePush,
    disablePush,
    fetchPushPrefs,
    savePushPrefs,
} from '../utils/pushClient';

const EXPENSE_TYPES = [
    { value: 'rent', label: 'Rent' },
    { value: 'emi', label: 'EMI / Loan' },
    { value: 'sip', label: 'SIP' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'other', label: 'Other bill' },
];

function IconEdit() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
    );
}

function IconTrash() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
    );
}

/**
 * Profile — money setup with obvious actions (verb-first).
 */
export default function SettingsView() {
    const {
        theme,
        toggleTheme,
        clearAllTransactions,
        clearTransactionsBySources,
        wipeAllLocalData,
        isSampleMode,
        sampleFixedExpenses,
        sampleGoals,
        sampleIncome,
        triggerSampleLocked,
        setAppScreen,
        setActiveTab,
        user,
        allTransactions,
        setStatus,
        handleLogin,
    } = useAppContext();

    const dbFixed = useLiveQuery(() => db.fixed_expenses.toArray(), []) || [];
    const dbGoals = useLiveQuery(() => db.goals.toArray(), []) || [];
    const overrides = useLiveQuery(() => db.user_overrides.toArray(), []) || [];
    const overrideCount = overrides.length;

    const fixedExpenses = isSampleMode ? (sampleFixedExpenses || []) : dbFixed;
    const goals = isSampleMode ? (sampleGoals || []) : dbGoals;
    const isPro = isProUnlocked(user) || isProEntitled(user);

    const [income, setIncome] = useState(() => {
        if (isSampleMode && sampleIncome) return String(sampleIncome);
        return localStorage.getItem('spenderly_monthly_income') || '';
    });
    const [saved, setSaved] = useState(false);
    const [waitlistOpen, setWaitlistOpen] = useState(false);
    const [proCheckoutOpen, setProCheckoutOpen] = useState(false);
    const [confirmClear, setConfirmClear] = useState(false);
    const [proClearAck, setProClearAck] = useState(false);
    const [clearMode, setClearMode] = useState('txns'); // txns | full
    const [clearScope, setClearScope] = useState('sources'); // sources | all — Pro only
    const [clearSources, setClearSources] = useState({ phonepe: false, gpay: false, paytm: false, manual: false });
    const [clearConfirmText, setClearConfirmText] = useState('');
    const [panel, setPanel] = useState(null);

    const [pushBusy, setPushBusy] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushPrefs, setPushPrefs] = useState({
        mid_month_upload: true,
        goal_nudges: true,
        money_checkin: true,
        personalise: false,
        goal_title: '',
    });

    const [expForm, setExpForm] = useState({ name: '', amount: '', type: 'rent' });
    const [goalForm, setGoalForm] = useState({ name: '', targetAmount: '', savedSoFar: '', deadline: '' });

    const [editingRule, setEditingRule] = useState(null);
    const [editCat, setEditCat] = useState('');
    const [editSub, setEditSub] = useState('');

    const [vaultMode, setVaultMode] = useState(null); // export | import | null
    const [vaultPw, setVaultPw] = useState('');
    const [vaultPw2, setVaultPw2] = useState('');
    const [vaultBusy, setVaultBusy] = useState(false);
    const [confirmImport, setConfirmImport] = useState(false);
    const importFileRef = useRef(null);
    const pendingImportRef = useRef(null);

    const openProGate = () => {
        if (apiConfigured()) setProCheckoutOpen(true);
        else setWaitlistOpen(true);
    };

    useEffect(() => {
        if (!isPro || !getSessionToken()) return undefined;
        let cancelled = false;
        (async () => {
            try {
                if (pushSupported() && 'serviceWorker' in navigator) {
                    const reg = await navigator.serviceWorker.ready;
                    const sub = await reg.pushManager.getSubscription();
                    if (!cancelled) setPushEnabled(Boolean(sub));
                }
                const data = await fetchPushPrefs();
                const prefs = data?.prefs || data;
                if (prefs && !cancelled) {
                    setPushPrefs((p) => ({
                        ...p,
                        mid_month_upload: prefs.mid_month_upload !== false,
                        goal_nudges: prefs.goal_nudges !== false,
                        money_checkin: prefs.money_checkin !== false,
                        personalise: !!prefs.personalise,
                        goal_title: prefs.goal_title || '',
                    }));
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [isPro]);

    const saveIncome = () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        localStorage.setItem('spenderly_monthly_income', String(parseFloat(income) || 0));
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
    };

    const addExpense = async () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        const amount = parseFloat(expForm.amount);
        if (!expForm.name.trim() || !amount || amount <= 0) return;
        await db.fixed_expenses.add({
            name: expForm.name.trim(),
            amount,
            type: expForm.type,
            frequency: 'monthly',
            lastPaidDate: new Date().toISOString().split('T')[0],
            isAutoDetected: false,
        });
        setExpForm({ name: '', amount: '', type: 'rent' });
        setPanel(null);
    };

    const removeExpense = async (id) => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        await db.fixed_expenses.delete(id);
    };

    const addGoal = async () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        if (goals.length >= 1) {
            openProGate();
            return;
        }
        const target = parseFloat(goalForm.targetAmount);
        if (!goalForm.name.trim() || !target) return;
        await db.goals.add({
            name: goalForm.name.trim(),
            targetAmount: target,
            savedSoFar: parseFloat(goalForm.savedSoFar) || 0,
            deadline: goalForm.deadline || new Date(Date.now() + 90 * 86400000).toISOString().split('T')[0],
        });
        setGoalForm({ name: '', targetAmount: '', savedSoFar: '', deadline: '' });
        setPanel(null);
    };

    const removeGoal = async (id) => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        await db.goals.delete(id);
    };

    const sourceCounts = (() => {
        const counts = { phonepe: 0, gpay: 0, paytm: 0, manual: 0, other: 0 };
        for (const t of allTransactions || []) {
            const s = t.source;
            if (s === 'phonepe' || s === 'gpay' || s === 'paytm' || s === 'manual') counts[s] += 1;
            else counts.other += 1;
        }
        return counts;
    })();

    const selectedClearSources = Object.entries(clearSources)
        .filter(([, on]) => on)
        .map(([k]) => k);

    const pendingClearCount = isPro && clearScope === 'sources'
        ? selectedClearSources.reduce((n, k) => n + (sourceCounts[k] || 0), 0)
        : (allTransactions?.length || 0);

    const clearPhraseOk = clearConfirmText.trim().toUpperCase() === 'CLEAR';

    const resetClearUi = () => {
        setConfirmClear(false);
        setProClearAck(false);
        setClearMode('txns');
        setClearScope('sources');
        setClearSources({ phonepe: false, gpay: false, paytm: false, manual: false });
        setClearConfirmText('');
    };

    const clearData = async () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        if (!clearPhraseOk) {
            setStatus({ active: true, text: 'Type CLEAR to confirm — this cannot be undone.', type: 'error' });
            return;
        }
        if (isPro && !proClearAck) {
            setStatus({ active: true, text: 'Export your vault first, then tick the acknowledgement.', type: 'error' });
            return;
        }
        if (isPro && clearScope === 'sources' && !selectedClearSources.length) {
            setStatus({ active: true, text: 'Pick at least one upload source to remove.', type: 'error' });
            return;
        }
        if (pendingClearCount === 0 && clearMode !== 'full') {
            setStatus({ active: true, text: 'Nothing to clear for that selection.', type: 'error' });
            return;
        }

        const wipingEverything = !isPro || clearScope === 'all' || clearMode === 'full';

        if (isPro && clearMode === 'full') {
            await wipeAllLocalData();
            localStorage.removeItem('spenderly_reality_done');
            localStorage.removeItem('spenderly_leak_targets_set');
            setStatus({ active: true, text: 'Full reset done. Import a vault if you need data back.', type: 'success' });
            resetClearUi();
            setAppScreen('reality');
            return;
        }

        if (isPro && clearScope === 'sources') {
            const n = await clearTransactionsBySources(selectedClearSources);
            setStatus({
                active: true,
                text: `Removed ${n} payment${n === 1 ? '' : 's'} from ${selectedClearSources.join(', ')}. Rules and vault options stay.`,
                type: 'success',
            });
            resetClearUi();
            setActiveTab('home');
            return;
        }

        await clearAllTransactions();
        setStatus({
            active: true,
            text: wipingEverything
                ? `All ${pendingClearCount} payments deleted from this device. This cannot be undone.`
                : 'Transactions cleared.',
            type: 'success',
        });
        resetClearUi();
        if (wipingEverything) {
            localStorage.removeItem('spenderly_reality_done');
            localStorage.removeItem('spenderly_leak_targets_set');
            setAppScreen('reality');
        } else {
            setActiveTab('home');
        }
    };

    const startEditRule = (row) => {
        setEditingRule(row);
        setEditCat(row.category || '');
        setEditSub(row.subcategory || '');
    };

    const saveEditRule = async () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        if (!editingRule || !editCat.trim() || !editSub.trim()) return;
        const userId = editingRule.user_id || (user ? user.id : 'guest-user');
        await handleRetag({
            userId,
            merchantKey: editingRule.merchant_key,
            newCategory: editCat.trim(),
            newSubcategory: editSub.trim(),
            applyToAll: true,
        });
        setEditingRule(null);
        setStatus({ active: true, text: 'Rule updated · matching payments re-tagged.', type: 'success' });
    };

    const deleteRule = async (row) => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        await db.user_overrides.delete([row.user_id, row.merchant_key]);
        setStatus({ active: true, text: 'Rule removed. Existing tags stay; new payments won’t auto-match.', type: 'success' });
    };

    const exportCsv = () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        if (!allTransactions.length) {
            setStatus({ active: true, text: 'No transactions to export yet.', type: 'error' });
            return;
        }
        downloadCsv(allTransactions);
        setStatus({ active: true, text: 'CSV downloaded.', type: 'success' });
    };

    const runVaultExport = async () => {
        if (isSampleMode) { triggerSampleLocked(); return; }
        if (!isPro) { openProGate(); return; }
        if (vaultPw.length < 6) {
            setStatus({ active: true, text: 'Password must be at least 6 characters.', type: 'error' });
            return;
        }
        if (vaultPw !== vaultPw2) {
            setStatus({ active: true, text: 'Passwords do not match.', type: 'error' });
            return;
        }
        setVaultBusy(true);
        try {
            const payload = await buildVaultPayload();
            const blob = await encryptVault(vaultPw, payload);
            downloadBlob(blob, vaultFilename());
            setStatus({ active: true, text: 'Encrypted vault downloaded. Keep the password — we can’t recover it.', type: 'success' });
            setVaultMode(null);
            setVaultPw('');
            setVaultPw2('');
        } catch (e) {
            setStatus({ active: true, text: e.message || 'Export failed', type: 'error' });
        } finally {
            setVaultBusy(false);
        }
    };

    const onImportFilePicked = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (isSampleMode) { triggerSampleLocked(); return; }
        if (!isPro) { openProGate(); return; }
        if (vaultPw.length < 6) {
            setStatus({ active: true, text: 'Enter the vault password first.', type: 'error' });
            return;
        }
        setVaultBusy(true);
        try {
            const buf = await file.arrayBuffer();
            const payload = await decryptVault(vaultPw, buf);
            pendingImportRef.current = payload;
            setConfirmImport(true);
        } catch (err) {
            setStatus({ active: true, text: err.message || 'Import failed', type: 'error' });
        } finally {
            setVaultBusy(false);
        }
    };

    const confirmVaultImport = async () => {
        const payload = pendingImportRef.current;
        if (!payload) return;
        setVaultBusy(true);
        try {
            await restoreVault(payload);
            if (payload.user?.id) {
                handleLogin(payload.user);
            } else {
                handleLogin({ id: 'guest-user', name: 'Guest', email: '', plan: isPro ? 'pro' : 'free' });
            }
            setConfirmImport(false);
            pendingImportRef.current = null;
            setVaultMode(null);
            setVaultPw('');
            setVaultPw2('');
            setStatus({ active: true, text: 'Vault restored. Reloading…', type: 'success' });
            setTimeout(() => window.location.reload(), 600);
        } catch (err) {
            setStatus({ active: true, text: err.message || 'Restore failed', type: 'error' });
        } finally {
            setVaultBusy(false);
        }
    };

    const fixedTotal = fixedExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const subs = fixedExpenses.filter(e => e.type === 'subscription' || e.isAutoDetected);
    const bills = fixedExpenses.filter(e => e.type !== 'subscription' && !e.isAutoDetected);
    const categories = getAllCategories();
    const editSubs = getSubcategories(editCat);

    const sortedOverrides = [...overrides].sort((a, b) =>
        String(a.merchant_key || '').localeCompare(String(b.merchant_key || ''))
    );

    return (
        <div className="money-profile">
            <p className="leak-eyebrow">Profile</p>
            <h1 className="leak-display page-title">Money setup</h1>
            <p className="leak-sub">Sets your ₹/day.</p>

            <section className="mp-card">
                <p className="mp-label">Monthly take-home income</p>
                <p className="mp-value">{income ? formatINR(parseFloat(income) || 0) : 'Not set yet'}</p>
                <div className="mp-income-edit">
                    <input
                        className="sp-input"
                        type="number"
                        inputMode="numeric"
                        value={income}
                        onChange={e => setIncome(e.target.value)}
                        placeholder="e.g. 55000"
                        aria-label="Monthly income"
                    />
                    <button type="button" className="sp-btn-primary mp-inline-btn" onClick={saveIncome}>
                        {saved ? 'Saved' : 'Save income'}
                    </button>
                </div>
            </section>

            <section className="mp-card">
                <div className="mp-card-head">
                    <div>
                        <p className="mp-label">Fixed every month</p>
                        <p className="mp-value">{formatINR(fixedTotal)}</p>
                    </div>
                    <button type="button" className="btn-pill" onClick={() => setPanel(panel === 'expense' ? null : 'expense')}>
                        {panel === 'expense' ? 'Cancel' : '+ Add bill'}
                    </button>
                </div>

                {panel === 'expense' && (
                    <div className="mp-form">
                        <input className="sp-input" placeholder="Name (e.g. House rent)" value={expForm.name} onChange={e => setExpForm(f => ({ ...f, name: e.target.value }))} />
                        <input className="sp-input" type="number" inputMode="numeric" placeholder="Amount in ₹" value={expForm.amount} onChange={e => setExpForm(f => ({ ...f, amount: e.target.value }))} />
                        <select className="sp-input" value={expForm.type} onChange={e => setExpForm(f => ({ ...f, type: e.target.value }))}>
                            {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <button type="button" className="sp-btn-primary" onClick={addExpense}>Save this bill</button>
                    </div>
                )}

                {bills.length > 0 && (
                    <div className="mp-list-block">
                        <p className="mp-list-label">Bills &amp; commitments</p>
                        <ul className="mp-list">
                            {bills.map((e, i) => (
                                <li key={e.id || `b-${i}`}>
                                    <div>
                                        <strong>{e.name}</strong>
                                        <span className="mp-meta">{e.type}</span>
                                    </div>
                                    <div className="mp-list-right">
                                        <span className="mp-list-amt">{formatINR(e.amount)}</span>
                                        {e.id != null && (
                                            <button
                                                type="button"
                                                className="mp-icon-btn danger"
                                                onClick={() => removeExpense(e.id)}
                                                aria-label={`Remove ${e.name}`}
                                                title="Remove"
                                            >
                                                <IconTrash />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {subs.length > 0 && (
                    <div className="mp-list-block">
                        <p className="mp-list-label">Subscriptions</p>
                        <ul className="mp-list">
                            {subs.map((e, i) => (
                                <li key={e.id || `s-${i}`}>
                                    <div>
                                        <strong>{e.name}</strong>
                                        {e.isAutoDetected && <span className="mp-pill">from statement</span>}
                                    </div>
                                    <div className="mp-list-right">
                                        <span className="mp-list-amt">{formatINR(e.amount)}</span>
                                        {e.id != null && (
                                            <button
                                                type="button"
                                                className="mp-icon-btn danger"
                                                onClick={() => removeExpense(e.id)}
                                                aria-label={`Remove ${e.name}`}
                                                title="Remove"
                                            >
                                                <IconTrash />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {fixedExpenses.length === 0 && panel !== 'expense' && (
                    <button type="button" className="sp-btn-ghost" onClick={() => setPanel('expense')}>
                        Add your rent or EMI
                    </button>
                )}
            </section>

            <section className="mp-card">
                <div className="mp-card-head">
                    <div>
                        <p className="mp-label">Savings goals</p>
                        <p className="mp-value">{goals.length ? `${goals.length} active` : 'None yet'}</p>
                    </div>
                    <button
                        type="button"
                        className="btn-pill"
                        onClick={() => {
                            if (!isSampleMode && goals.length >= 1) {
                                openProGate();
                                return;
                            }
                            setPanel(panel === 'goal' ? null : 'goal');
                        }}
                    >
                        {panel === 'goal' ? 'Cancel' : goals.length >= 1 ? '+ More (Pro)' : '+ Add goal'}
                    </button>
                </div>
                <p className="mp-hint">1 goal on Free.</p>

                {panel === 'goal' && (
                    <div className="mp-form">
                        <input className="sp-input" placeholder="Goal name (e.g. Goa trip)" value={goalForm.name} onChange={e => setGoalForm(f => ({ ...f, name: e.target.value }))} />
                        <input className="sp-input" type="number" inputMode="numeric" placeholder="Target ₹" value={goalForm.targetAmount} onChange={e => setGoalForm(f => ({ ...f, targetAmount: e.target.value }))} />
                        <input className="sp-input" type="number" inputMode="numeric" placeholder="Already saved ₹" value={goalForm.savedSoFar} onChange={e => setGoalForm(f => ({ ...f, savedSoFar: e.target.value }))} />
                        <input className="sp-input" type="date" value={goalForm.deadline} onChange={e => setGoalForm(f => ({ ...f, deadline: e.target.value }))} />
                        <button type="button" className="sp-btn-primary" onClick={addGoal}>Save this goal</button>
                    </div>
                )}

                <ul className="mp-list">
                    {goals.map((g, i) => {
                        const monthly = calcGoalMonthly(g.targetAmount, g.savedSoFar, g.deadline);
                        const months = getMonthsUntil(g.deadline);
                        const pct = g.targetAmount > 0 ? Math.min(100, Math.round(((g.savedSoFar || 0) / g.targetAmount) * 100)) : 0;
                        return (
                            <li key={g.id || i} className="mp-goal-item">
                                <div className="mp-goal-row">
                                    <strong>{g.name}</strong>
                                    <div className="mp-list-right">
                                        <span className="mp-list-amt">{formatINR(monthly)}/mo</span>
                                        {g.id != null && (
                                            <button
                                                type="button"
                                                className="mp-icon-btn danger"
                                                onClick={() => removeGoal(g.id)}
                                                aria-label={`Remove ${g.name}`}
                                                title="Remove"
                                            >
                                                <IconTrash />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="home-gauge-track">
                                    <div className="home-gauge-fill" style={{ width: `${pct}%` }} />
                                </div>
                                <div className="mp-goal-meta">
                                    <span>{formatINR(g.savedSoFar || 0)} / {formatINR(g.targetAmount)}{months > 0 ? ` · ${months} mo left` : ''}</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </section>

            <section className="mp-card">
                <div className="mp-card-head">
                    <div>
                        <p className="mp-label">Remembered merchants</p>
                        <p className="mp-value">{overrideCount ? `${overrideCount} rule${overrideCount === 1 ? '' : 's'}` : 'None yet'}</p>
                    </div>
                    <button type="button" className="btn-pill" onClick={() => setActiveTab('activity')}>
                        Fix on Spend
                    </button>
                </div>
                <p className="mp-hint">Same name auto-tags on the next statement. Use edit to change a category.</p>

                {sortedOverrides.length === 0 ? (
                    <p className="mp-empty-hint">Re-tag a payment on Spend to teach Spenderly.</p>
                ) : (
                    <ul className="mp-list mp-rules-list">
                        {sortedOverrides.map((row) => (
                            <li key={`${row.user_id}-${row.merchant_key}`}>
                                {editingRule?.merchant_key === row.merchant_key && editingRule?.user_id === row.user_id ? (
                                    <div className="mp-form mp-rule-edit">
                                        <strong className="mp-rule-key">{row.merchant_key}</strong>
                                        <select className="sp-input" value={editCat} onChange={e => {
                                            setEditCat(e.target.value);
                                            const subs = getSubcategories(e.target.value);
                                            setEditSub(subs[0] || '');
                                        }}>
                                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <select className="sp-input" value={editSub} onChange={e => setEditSub(e.target.value)}>
                                            {editSubs.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                        <div className="mp-rule-actions">
                                            <button type="button" className="sp-btn-primary" onClick={saveEditRule}>Save rule</button>
                                            <button type="button" className="sp-btn-ghost" onClick={() => setEditingRule(null)}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mp-rule-main">
                                            <strong>{row.merchant_key}</strong>
                                            <span className="mp-meta">{row.category} · {row.subcategory}</span>
                                        </div>
                                        <div className="mp-list-right mp-list-actions">
                                            <button
                                                type="button"
                                                className="mp-icon-btn"
                                                onClick={() => startEditRule(row)}
                                                aria-label={`Edit category for ${row.merchant_key}`}
                                                title="Edit"
                                            >
                                                <IconEdit />
                                            </button>
                                            <button
                                                type="button"
                                                className="mp-icon-btn danger"
                                                onClick={() => deleteRule(row)}
                                                aria-label={`Remove rule for ${row.merchant_key}`}
                                                title="Remove"
                                            >
                                                <IconTrash />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="mp-actions">
                <button type="button" className="action-row" onClick={() => setAppScreen('targets')}>
                    <span className="action-row-text">
                        <strong>Leak limits</strong>
                    </span>
                    <span className="action-row-chevron" aria-hidden>›</span>
                </button>
                <button type="button" className="action-row" onClick={exportCsv}>
                    <span className="action-row-text">
                        <strong>Export CSV</strong>
                        <span>All transactions on this device</span>
                    </span>
                    <span className="action-row-chevron" aria-hidden>›</span>
                </button>
                <button
                    type="button"
                    className="action-row"
                    onClick={() => window.dispatchEvent(new CustomEvent('spenderly:request-install'))}
                >
                    <span className="action-row-text">
                        <strong>Install app</strong>
                        <span>Add Spenderly to your home screen</span>
                    </span>
                    <span className="action-row-chevron" aria-hidden>›</span>
                </button>
                <div className="action-row appearance-row">
                    <span className="action-row-text">
                        <strong>Theme</strong>
                    </span>
                    <div className="theme-toggle" role="group" aria-label="Theme">
                        <button type="button" className={`theme-seg ${theme === 'dark' ? 'active' : ''}`} onClick={() => { if (theme !== 'dark') toggleTheme(); }}>Dark</button>
                        <button type="button" className={`theme-seg ${theme === 'light' ? 'active' : ''}`} onClick={() => { if (theme !== 'light') toggleTheme(); }}>Light</button>
                    </div>
                </div>
            </section>

            <section className="mp-pro">
                <span className="pro-badge">PRO</span>
                <h3>Full history · encrypted vault · installable</h3>
                <p>
                    {isPro
                        ? 'Keep every month. Re-uploads merge — we never wipe Pro history. Export an encrypted vault before clearing or switching phones. Use Install app above for the home-screen PWA.'
                        : 'Free keeps ~30 days. Pro keeps full months, blocks history wipes on re-upload, and adds an encrypted vault you move yourself. Early bird ₹499 · then ₹999 lifetime.'}
                </p>

                {isPro ? (
                    <div className="mp-vault">
                        <div className="mp-vault-btns">
                            <button
                                type="button"
                                className="sp-btn-primary"
                                onClick={() => { setVaultMode(vaultMode === 'export' ? null : 'export'); setVaultPw(''); setVaultPw2(''); }}
                            >
                                {vaultMode === 'export' ? 'Cancel' : 'Export vault'}
                            </button>
                            <button
                                type="button"
                                className="sp-btn-ghost"
                                onClick={() => { setVaultMode(vaultMode === 'import' ? null : 'import'); setVaultPw(''); setVaultPw2(''); }}
                            >
                                {vaultMode === 'import' ? 'Cancel' : 'Import vault'}
                            </button>
                        </div>

                        {vaultMode === 'export' && (
                            <div className="mp-form">
                                <input
                                    className="sp-input"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Vault password (min 6)"
                                    value={vaultPw}
                                    onChange={e => setVaultPw(e.target.value)}
                                />
                                <input
                                    className="sp-input"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="Confirm password"
                                    value={vaultPw2}
                                    onChange={e => setVaultPw2(e.target.value)}
                                />
                                <button type="button" className="sp-btn-primary" disabled={vaultBusy} onClick={runVaultExport}>
                                    {vaultBusy ? 'Encrypting…' : 'Download .spenderly'}
                                </button>
                            </div>
                        )}

                        {vaultMode === 'import' && (
                            <div className="mp-form">
                                <p className="mp-hint">Replaces transactions, rules, goals, and budgets on this device.</p>
                                <input
                                    className="sp-input"
                                    type="password"
                                    autoComplete="current-password"
                                    placeholder="Vault password"
                                    value={vaultPw}
                                    onChange={e => setVaultPw(e.target.value)}
                                />
                                <input
                                    ref={importFileRef}
                                    type="file"
                                    accept=".spenderly,application/octet-stream"
                                    hidden
                                    onChange={onImportFilePicked}
                                />
                                <button
                                    type="button"
                                    className="sp-btn-primary"
                                    disabled={vaultBusy}
                                    onClick={() => importFileRef.current?.click()}
                                >
                                    {vaultBusy ? 'Reading…' : 'Choose .spenderly file'}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="mp-vault-btns" style={{ flexDirection: 'column', gap: 8 }}>
                        <button type="button" className="sp-btn-primary" onClick={openProGate}>
                            Get Pro · ₹499 / ₹999
                        </button>
                        <button type="button" className="sp-btn-ghost" onClick={() => setWaitlistOpen(true)}>
                            Notify me instead
                        </button>
                    </div>
                )}
            </section>

            {isPro && getSessionToken() && (
                <section className="mp-card">
                    <p className="mp-label">Reminders</p>
                    <p className="mp-hint">
                        Quiet nudges only (a few per month). iOS needs Add to Home Screen. Android Chrome works best.
                        {getProAccount()?.email ? ` Signed in as ${getProAccount().email}.` : ''}
                    </p>
                    <div className="mp-vault-btns" style={{ marginBottom: 12 }}>
                        <button
                            type="button"
                            className="sp-btn-primary"
                            disabled={pushBusy || !pushSupported()}
                            onClick={async () => {
                                setPushBusy(true);
                                try {
                                    if (pushEnabled) {
                                        await disablePush();
                                        setPushEnabled(false);
                                        setStatus({ active: true, text: 'Reminders off', type: 'success' });
                                    } else {
                                        await enablePush();
                                        setPushEnabled(true);
                                        setStatus({ active: true, text: 'Reminders enabled', type: 'success' });
                                    }
                                    setTimeout(() => setStatus({ active: false }), 2000);
                                } catch (e) {
                                    setStatus({ active: true, text: e.message || 'Could not update notifications', type: 'error' });
                                    setTimeout(() => setStatus({ active: false }), 3500);
                                } finally {
                                    setPushBusy(false);
                                }
                            }}
                        >
                            {pushBusy ? '…' : pushEnabled ? 'Disable notifications' : 'Enable notifications'}
                        </button>
                    </div>
                    {[
                        { key: 'mid_month_upload', label: 'Mid-month statement upload' },
                        { key: 'goal_nudges', label: 'Goal pace nudges' },
                        { key: 'money_checkin', label: 'Money check-in' },
                    ].map((row) => (
                        <label key={row.key} className="retag-checkbox-label mp-clear-ack">
                            <input
                                type="checkbox"
                                checked={!!pushPrefs[row.key]}
                                onChange={async (e) => {
                                    const next = { ...pushPrefs, [row.key]: e.target.checked };
                                    setPushPrefs(next);
                                    try {
                                        await savePushPrefs(next);
                                    } catch { /* ignore offline */ }
                                }}
                            />
                            <span>{row.label}</span>
                        </label>
                    ))}
                    <label className="retag-checkbox-label mp-clear-ack">
                        <input
                            type="checkbox"
                            checked={!!pushPrefs.personalise}
                            onChange={async (e) => {
                                const next = { ...pushPrefs, personalise: e.target.checked };
                                setPushPrefs(next);
                                try {
                                    await savePushPrefs(next);
                                } catch { /* ignore */ }
                            }}
                        />
                        <span>Personalise reminders (store goal title on server)</span>
                    </label>
                    {pushPrefs.personalise && (
                        <input
                            className="sp-input"
                            placeholder="Goal title for nudges"
                            value={pushPrefs.goal_title || ''}
                            onChange={(e) => setPushPrefs((p) => ({ ...p, goal_title: e.target.value }))}
                            onBlur={async () => {
                                try { await savePushPrefs(pushPrefs); } catch { /* ignore */ }
                            }}
                        />
                    )}
                </section>
            )}

            <p className="mp-hint legal-links" style={{ textAlign: 'center', margin: '8px 0 16px' }}>
                <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
                {' · '}
                <a href="/terms.html" target="_blank" rel="noreferrer">Terms</a>
            </p>

            <div className="settings-danger">
                {!confirmClear ? (
                    <button
                        type="button"
                        className="sp-btn-ghost danger"
                        onClick={() => {
                            setConfirmClear(true);
                            setProClearAck(false);
                            setClearMode('txns');
                            setClearScope(isPro ? 'sources' : 'all');
                            setClearSources({ phonepe: false, gpay: false, paytm: false, manual: false });
                            setClearConfirmText('');
                        }}
                    >
                        Clear transactions…
                    </button>
                ) : (
                    <div className="mp-clear-panel" role="region" aria-labelledby="clear-txn-title">
                        <h3 id="clear-txn-title" className="mp-clear-title">Delete payments permanently</h3>
                        <p className="mp-clear-warn">
                            This removes data from <strong>this device only</strong>. There is no undo
                            {isPro ? ' unless you already exported a vault' : ''}.
                            {allTransactions?.length
                                ? ` You currently have ${allTransactions.length} payment${allTransactions.length === 1 ? '' : 's'}.`
                                : ' You have no payments stored.'}
                        </p>

                        {isPro ? (
                            <>
                                <p className="mp-label">What to delete</p>
                                <div className="mp-clear-scopes" role="radiogroup" aria-label="Clear scope">
                                    <label className={`mp-clear-scope ${clearScope === 'sources' ? 'active' : ''}`}>
                                        <input
                                            type="radio"
                                            name="clear-scope"
                                            checked={clearScope === 'sources'}
                                            onChange={() => { setClearScope('sources'); setClearMode('txns'); }}
                                        />
                                        <span>
                                            <strong>Selected uploads</strong>
                                            <em>Remove PhonePe / GPay / Paytm / manual only</em>
                                        </span>
                                    </label>
                                    <label className={`mp-clear-scope ${clearScope === 'all' ? 'active' : ''}`}>
                                        <input
                                            type="radio"
                                            name="clear-scope"
                                            checked={clearScope === 'all'}
                                            onChange={() => setClearScope('all')}
                                        />
                                        <span>
                                            <strong>Every transaction ever</strong>
                                            <em>Wipe the full payment history on this phone</em>
                                        </span>
                                    </label>
                                </div>

                                {clearScope === 'sources' && (
                                    <div className="mp-clear-sources">
                                        {[
                                            { key: 'phonepe', label: 'PhonePe uploads' },
                                            { key: 'gpay', label: 'GPay uploads' },
                                            { key: 'paytm', label: 'Paytm uploads' },
                                            { key: 'manual', label: 'Manual entries' },
                                        ].map((row) => (
                                            <label key={row.key} className="retag-checkbox-label mp-clear-ack">
                                                <input
                                                    type="checkbox"
                                                    checked={!!clearSources[row.key]}
                                                    disabled={!sourceCounts[row.key]}
                                                    onChange={(e) => setClearSources((s) => ({ ...s, [row.key]: e.target.checked }))}
                                                />
                                                <span>
                                                    {row.label}
                                                    <em className="mp-clear-count"> {sourceCounts[row.key] || 0}</em>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {clearScope === 'all' && (
                                    <label className="retag-checkbox-label mp-clear-ack">
                                        <input
                                            type="checkbox"
                                            checked={clearMode === 'full'}
                                            onChange={(e) => setClearMode(e.target.checked ? 'full' : 'txns')}
                                        />
                                        <span>Also wipe rules, goals &amp; budgets (full device reset)</span>
                                    </label>
                                )}

                                <label className="retag-checkbox-label mp-clear-ack">
                                    <input
                                        type="checkbox"
                                        checked={proClearAck}
                                        onChange={(e) => setProClearAck(e.target.checked)}
                                    />
                                    <span>I exported my vault, or I accept permanent loss of the selected payments</span>
                                </label>
                            </>
                        ) : (
                            <p className="mp-hint">
                                Free clears every payment on this device. Remembered merchant rules stay.
                                Upgrade to Pro to delete one app&apos;s uploads at a time.
                            </p>
                        )}

                        <label className="sp-field-label" htmlFor="clear-confirm-type">
                            Type <strong>CLEAR</strong> to continue
                            {pendingClearCount > 0 ? ` (${pendingClearCount} will be deleted)` : ''}
                        </label>
                        <input
                            id="clear-confirm-type"
                            className="sp-input"
                            autoComplete="off"
                            placeholder="CLEAR"
                            value={clearConfirmText}
                            onChange={(e) => setClearConfirmText(e.target.value)}
                        />

                        <div className="leak-actions">
                            <button
                                type="button"
                                className="sp-btn-primary"
                                style={{ background: 'var(--danger)' }}
                                disabled={
                                    !clearPhraseOk
                                    || (isPro && !proClearAck)
                                    || (isPro && clearScope === 'sources' && !selectedClearSources.length)
                                    || (pendingClearCount === 0 && clearMode !== 'full')
                                }
                                onClick={clearData}
                            >
                                {clearMode === 'full'
                                    ? 'Delete everything'
                                    : clearScope === 'sources' && isPro
                                        ? `Delete ${pendingClearCount || 0} selected`
                                        : `Delete all ${pendingClearCount || 0} payments`}
                            </button>
                            <button
                                type="button"
                                className="sp-btn-ghost"
                                onClick={resetClearUi}
                            >
                                Keep my data
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {confirmImport && (
                <div className="sp-modal-overlay" role="presentation" onClick={() => setConfirmImport(false)}>
                    <div className="sp-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>Replace this device’s data?</h3>
                        <p className="sp-modal-message" style={{ marginBottom: 20 }}>
                            Import overwrites transactions, remembered merchants, goals, and budgets. This cannot be undone unless you have another vault.
                        </p>
                        <div className="sp-modal-actions">
                            <button type="button" className="sp-btn-ghost" onClick={() => { setConfirmImport(false); pendingImportRef.current = null; }}>
                                Cancel
                            </button>
                            <button type="button" className="sp-btn-primary" disabled={vaultBusy} onClick={confirmVaultImport}>
                                {vaultBusy ? 'Restoring…' : 'Replace & reload'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {waitlistOpen && (
                <WaitlistModal
                    onClose={() => setWaitlistOpen(false)}
                    source="settings-pro"
                    featureTitle="Full history & encrypted vault"
                />
            )}
            {proCheckoutOpen && (
                <ProCheckoutModal
                    source="settings-pro"
                    onClose={() => setProCheckoutOpen(false)}
                    onActivated={({ user: proUser }) => {
                        if (proUser && handleLogin) {
                            const next = {
                                ...(user || { id: 'guest-user', name: 'Guest', email: '' }),
                                plan: 'pro',
                                email: proUser.email || user?.email || '',
                                name: proUser.name || user?.name || 'Guest',
                            };
                            handleLogin(next);
                        }
                        setProCheckoutOpen(false);
                    }}
                />
            )}
        </div>
    );
}
